import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ProviderActions from './provider.actions';
import { catchError, map, mergeMap, of, tap } from 'rxjs';
import { ProviderService } from '../../services/moduleServices/provider.service';
import { Action } from '@ngrx/store';
import { Router } from '@angular/router';
import { NotificationService } from '../../services/notification.service';
import { NotificationMessages } from '../../messages/notification.messages';
import {
  extractBackendMessage,
  getErrorMessageFromStatus,
} from '../../utils/error-message.util';
import { HttpStatusCode } from '@angular/common/http';
import { DocumentService } from '../../services/moduleServices/document.service';

@Injectable()
export class ProviderEffects {
  registerSuperAdmin$: any;
  registerSuperAdminSuccess$: any;
  registerSuperAdminFailure$: any;
  loadAllProviders$: any;
  loadProviderById$: any;
  deleteProviderById$: any;
  deleteProviderSuccess$: any;
  deleteProviderFailure$: any;

  constructor(
    private readonly actions$: Actions,
    private readonly providerService: ProviderService,
    private readonly router: Router,
    private readonly notification: NotificationService,
    private readonly documentService: DocumentService,
  ) {
    // Register Super Admin
    this.registerSuperAdmin$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ProviderActions.registerSuperAdmin),
        mergeMap(({ requestBody, uploadedDocumentIds }) =>
          this.providerService.registerSuperAdmin(requestBody).pipe(
            map(
              (response) =>
                ProviderActions.registerSuperAdminSuccess({ response }),
              // this.router.navigate(['/user/provider-management'])
            ),
            catchError((error) =>
              of(
                ProviderActions.registerSuperAdminFailure({
                  error: error,
                  uploadedDocumentIds,
                }),
              ),
            ),
          ),
        ),
      ),
    );

    // Clear localStorage when registration succeeds
    this.registerSuperAdminSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(ProviderActions.registerSuperAdminSuccess),
          map(({ response }) => {
            // Clear uploaded-file cache
            localStorage.removeItem('fieldFileMap');

            //  Success Toast
            this.notification.showSuccess(
              NotificationMessages.provider.registerSuccess,
            );
            const payload = { providerId: response.Value };

            // this.providerService.linkProvider(payload).subscribe({
            //   next: () => {
            //     this.notification.showSuccess(
            //       NotificationMessages.provider.linkSuccess,
            //     );
            //   },
            //   error: (err) => {
            //     const message = getErrorMessageFromStatus(err, 'provider');
            //     this.notification.showError(message);
            //   },
            // });
            // Optional Navigation
            this.router.navigate(['/user/provider-management']);
          }),
        ),
      { dispatch: false },
    );

    this.registerSuperAdminFailure$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(ProviderActions.registerSuperAdminFailure),
          tap(async ({ error, uploadedDocumentIds }) => {
            console.error('Registration Error:', error);

            //   ROLLBACK UPLOADED DOCUMENTS
            if (uploadedDocumentIds && uploadedDocumentIds.length > 0) {
              console.warn(
                'Rolling back uploaded documents:',
                uploadedDocumentIds,
              );

              this.documentService
                .deleteDocumentsBulk(uploadedDocumentIds)
                .subscribe({
                  next: () =>
                    console.log('Rollback success: documents deleted'),
                  error: (e) => console.error('Rollback failed:', e),
                });
            }

            //   Extract message
            const status = error?.StatusCode ?? error?.status ?? 0;
            let message = NotificationMessages.provider.registerError;
            const backendError = extractBackendMessage(error);

            switch (status) {
              case HttpStatusCode.BadRequest:
                message = backendError || message;
                break;
              case HttpStatusCode.Unauthorized:
                message = NotificationMessages.auth.unauthorized;
                break;
              case HttpStatusCode.Forbidden:
                message = NotificationMessages.auth.forbidden;
                break;
              case HttpStatusCode.NotFound:
                message = NotificationMessages.provider.notFound;
                break;
              case HttpStatusCode.InternalServerError:
                message = NotificationMessages.common.serverError;
                break;
              case HttpStatusCode.ServiceUnavailable:
                message = NotificationMessages.common.unavailable;
                break;
              case 0:
                message = NotificationMessages.common.networkError;
                break;
              default:
                break;
            }

            this.notification.showError(message, 'Registration Error');
          }),
        ),
      { dispatch: false },
    );

    //  Get All Providers
    this.loadAllProviders$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ProviderActions.loadAllProviders),
        mergeMap(({ filters }) =>
          this.providerService.getAllProviders(filters).pipe(
            map((response) => {
              const providers = response.Value?.Items || [];
              return ProviderActions.loadAllProvidersSuccess({ providers });
            }),
            catchError((error) =>
              of(
                ProviderActions.loadAllProvidersFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
        ),
      ),
    );

    //  Get Provider By ID
    this.loadProviderById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ProviderActions.loadProviderById),
        mergeMap(({ id }) =>
          this.providerService.getProviderById(id).pipe(
            map((response) => {
              const provider = response.Value; // ← Extract value safely
              return ProviderActions.loadProviderByIdSuccess({ provider });
            }),
            catchError((error) =>
              of(
                ProviderActions.loadProviderByIdFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
        ),
      ),
    );

    //  Delete Provider
    this.deleteProviderById$ = createEffect(() =>
      this.actions$.pipe(
        ofType(ProviderActions.deleteProviderById),
        mergeMap(({ id }) =>
          this.providerService.deleteProviderById(id).pipe(
            map((response) =>
              ProviderActions.deleteProviderByIdSuccess({ response }),
            ),
            catchError((error) =>
              of(
                ProviderActions.deleteProviderByIdFailure({
                  error: error.message,
                }),
              ),
            ),
          ),
        ),
      ),
    );

    //  Delete Success Toast
    this.deleteProviderSuccess$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(ProviderActions.deleteProviderByIdSuccess),
          map(() => {
            this.notification.showSuccess('Provider deleted successfully');
          }),
        ),
      { dispatch: false },
    );

    // FAILURE
    this.deleteProviderFailure$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(ProviderActions.deleteProviderByIdFailure),
          map(({ error }) => {
            this.notification.showError(error || 'Failed to delete provider');
          }),
        ),
      { dispatch: false },
    );
  }
}
